/**
 * Verifies the app's dependencies are installed before running a command that
 * needs them (dev, test, deploy, add).
 *
 * `create-deepspace` runs `npm install` (and `deepspace skills add`) in a
 * detached background process so the user gets their prompt back immediately.
 * The background worker writes three sentinels under `<appDir>/.deepspace/`:
 *
 *   install.started — created before the worker is spawned
 *   install.done    — written on successful completion
 *   install.err     — written on failure (contains the error message)
 *   install.log     — combined stdout/stderr of the install
 *
 * The presence of `node_modules/deepspace/package.json` is the ground truth
 * for "ready"; the sentinels only shape the error message.
 */
import { existsSync, readFileSync } from 'node:fs'
import { join } from 'node:path'

export function ensureInstallReady(appDir: string): void {
  if (existsSync(join(appDir, 'node_modules', 'deepspace', 'package.json'))) return

  const sentinelDir = join(appDir, '.deepspace')
  const errPath = join(sentinelDir, 'install.err')
  const startedPath = join(sentinelDir, 'install.started')
  const donePath = join(sentinelDir, 'install.done')
  const logPath = join(sentinelDir, 'install.log')

  if (existsSync(errPath)) {
    console.error('Background install failed:')
    console.error(readFileSync(errPath, 'utf-8').trimEnd())
    if (existsSync(logPath)) console.error(`\nFull log: ${logPath}`)
    console.error('\nRun `npm install` (or `bun install`) manually, then retry.')
    process.exit(1)
  }

  if (existsSync(startedPath) && !existsSync(donePath)) {
    console.error('Dependencies are still installing in the background.')
    if (existsSync(logPath)) console.error(`Tail progress: tail -f ${logPath}`)
    console.error('Retry once it finishes.')
    process.exit(1)
  }

  console.error('Dependencies not installed. Run `npm install` (or `bun install`) first.')
  process.exit(1)
}
